import { useCallback, useContext, useEffect, useState } from 'react'
import axios from 'axios'
import debounce from 'debounce'
import * as routes from '../../routes'
import { Input, AlertContext } from '../../widgets'
import globals from '../../globals'
import {
  beatSorts,
  beatSortTextToSearchSort,
  postSorts,
  postSortTextToSearchSort,
  Loading,
  rejectEmptyValues,
  playlistSorts,
  playlistSortTextToSearchSort,
} from '../../shared'
import NavigationTab from '../../components/NavigationTab'
import FilterPicker from './FilterPicker'
import AdminBeatCell from './AdminBeatCell'
import AdminPostCell from './AdminPostCell'
import AdminUserCell from './AdminUserCell'
import AdminPlaylistCell from './AdminPlaylistCell'
import AdminHashtagCell from './AdminHashtagCell'

const tabText = {
  'users': 'Users',
  'beats': 'Beats',
  'posts': 'Tracks',
  'playlists': 'Playlists',
  'hashtags': 'Hashtags',
}

const searchUrls = {
  'users': routes.adminSearchUsersUrl,
  'beats': routes.adminSearchBeatsUrl,
  'posts': routes.adminSearchPostsUrl,
  'playlists': routes.adminSearchPlaylistsUrl,
  'hashtags': routes.adminSearchHashtagsUrl,
}

const Search = (props) => {
  const [state, replaceState] = useState({
    loading: false,
    query: null,
    activeTab: null,

    users: [],
    beats: [],
    posts: [],
    playlists: [],
    hashtags: [],
    selectedSort: null,
    selectedGenres: props.defaultGenres || [],
    selectedBpms: props.defaultBpms || [],
    selectedKeys: props.defaultKeys || [],
    selectedCreatorTypes: props.defaultCreatorTypes || [],
  })

  const setState = (subState) => {
    replaceState(state => {
      return { ...state, ...subState }
    })
  }

  const { handleError } = useContext(AlertContext)

  useEffect(() => {
    search(state)
  }, [
    state.activeTab,
    state.selectedSort,
    state.selectedSort,
    state.selectedGenres,
    state.selectedBpms,
    state.selectedKeys,
    state.selectedCreatorTypes,
  ])

  useEffect(() => {
    handleTabChange('users')
  }, [])

  const handleTabChange = (tab) => {
    if (state.activeTab === tab) {
      return
    }
    setState({ activeTab: tab })
  }

  const search = (state) => {
    if (!state.activeTab) {
      return
    }
    setState({ loading: true })
    var url = searchUrls[state.activeTab]
    const body = rejectEmptyValues({
      query: state.query,
      filter: {
        genre: state.selectedGenres,
        bpm: state.selectedBpms,
        key: state.selectedKeys,
        creator_type: state.selectedCreatorTypes,
      }
    })
    var sort = null
    if (state.activeTab === 'beats') {
      sort = beatSortTextToSearchSort(state.selectedSort)
    } else if (state.activeTab === 'posts') {
      sort = postSortTextToSearchSort(state.selectedSort)
    } else if (state.activeTab === 'playlists') {
      sort = playlistSortTextToSearchSort(state.selectedSort)
    }
    if (sort) {
      body.sort = sort
    }
    axios.post(url, body).then((response) => {
      setState({ [state.activeTab]: response.data.data })
    }).catch((error) => {
      handleError(error)
    }).finally(() => {
      setState({ loading: false })
    })
  }

  const debouncedSearch = useCallback(debounce(search, 500), [])

  useEffect(() => {
    debouncedSearch(state)
  }, [state.query])

  const onInputChange = (query) => {
    setState({ query })
  }

  const onFilterChanged = (stateKey, checkedValues) => {
    setState({ [stateKey]: checkedValues })
  }

  const onSortChanged = (value) => {
    setState({ selectedSort: value })
  }

  const toggleFeatured = (item, url) => {
    axios.post(url, {
      id: item.id,
      featured: !item.is_featured,
    }).then((response) => {
      search(state)
    }).catch((error) => {
      handleError(error)
    })
  }
  const onFeatureBeat = (beat) => toggleFeatured(beat, routes.adminBeatFeaturedUrl)
  const onFeaturePost = (post) => toggleFeatured(post, routes.adminPostFeaturedUrl)
  const onFeaturePlaylist = (playlist) => toggleFeatured(playlist, routes.adminPlaylistFeaturedUrl)

  var sorts = null
  switch (state.activeTab) {
    case 'beats':
      sorts = beatSorts
      break
    case 'posts':
      sorts = postSorts
      break
    case 'playlists':
      sorts = playlistSorts
      break
    default:
      sorts = []
  }

  const hasSort = state.activeTab === 'beats' || state.activeTab === 'posts' || state.activeTab === 'playlists'

  const sortOption = hasSort && <div className='search-sort'>
    <h6>Sort</h6>
    <select
      defaultValue={sorts[0]}
      onChange={event => onSortChanged(event.target.value)}
    >
      {sorts.map((sort, index) =>
        <option key={index} className='select' value={sort}>{sort}</option>
      )}
    </select>
  </div>

  var filterPickers = null
  if (state.activeTab === 'users') {
    filterPickers =
      <div className='search-filters'>
        <FilterPicker.CreatorType defaultValues={props.defaultCreatorTypes} onChange={(values) => onFilterChanged('selectedCreatorTypes', values)} />
      </div>
  } else if (state.activeTab === 'beats') {
    filterPickers =
      <div className='search-filters'>
        <FilterPicker.Bpm defaultValues={props.defaultBpms} onChange={(values) => onFilterChanged('selectedBpms', values)} />
        <FilterPicker.Key defaultValues={props.defaultKeys} onChange={(values) => onFilterChanged('selectedKeys', values)} />
        <FilterPicker.Genre defaultValues={props.defaultGenres} choices={globals.genres} onChange={(values) => onFilterChanged('selectedGenres', values)} />
      </div>
  } else if (state.activeTab === 'posts') {
    filterPickers =
      <div className='search-filters'>
        <FilterPicker.Genre defaultValues={props.defaultGenres} choices={globals.genres} onChange={(values) => onFilterChanged('selectedGenres', values)} />
      </div>
  }

  var content = <Loading />
  if (!state.loading) {
    content =
      <div className='search-no-results'>
        No results found.
      </div>

    if (state.activeTab === 'users' && state.users.length > 0) {
      content =
        <div className='search-results'>
          {state.users.map((user, i) =>
            <AdminUserCell
              key={i}
              user={user}
              actions={props.userRowActionTitle && [
                { title: props.userRowActionTitle, onClick: () => props.userRowActionOnClick(user) },
              ]}
            />
          )}
        </div>

    } else if (state.activeTab === 'beats' && state.beats.length > 0) {
      content =
        <div className='search-results'>
          {state.beats.map((beat, i) =>
            <AdminBeatCell key={i} beat={beat}>
              <AdminBeatCell.FeatureActions beat={beat} onFeatureClick={onFeatureBeat} />
            </AdminBeatCell>
          )}
        </div>

    } else if (state.activeTab === 'posts' && state.posts.length > 0) {
      content =
        <div className='search-results'>
          {state.posts.map((post, i) =>
            <AdminPostCell key={i} post={post}>
              <AdminPostCell.FeatureActions post={post} onFeatureClick={onFeaturePost} />
            </AdminPostCell>
          )}
        </div>

    } else if (state.activeTab === 'playlists' && state.playlists.length > 0) {
      content =
        <div className='search-results'>
          {state.playlists.map((playlist, i) =>
            <AdminPlaylistCell key={i} playlist={playlist}>
              <AdminPlaylistCell.FeatureActions playlist={playlist} onFeatureClick={onFeaturePlaylist} />
            </AdminPlaylistCell>
          )}
        </div>
    } else if (state.activeTab === 'hashtags' && state.hashtags.length > 0) {
      content =
        <div className='search-results'>
          {state.hashtags.map((hashtag, i) =>
            <AdminHashtagCell key={i} hashtag={hashtag} />
          )}
        </div>
    }
  }

  return (
    <>
      <Input
        className='search-input'
        type='text'
        placeholder='Type here to start searching'
        onChange={(event) => onInputChange(event.target.value)}
      />
      <br />
      <br />
      <div className='moderator-content'>
        <div className='navigation-tabs'>
          {props.tabs.map((tab, index) =>
            <NavigationTab
              key={index}
              isActive={state.activeTab === tab}
              onClick={() => { handleTabChange(tab) }}
              text={tabText[tab]}
            />
          )}
        </div>
        {sortOption}
        <div className='search-container'>
          {filterPickers}
          {content}
        </div>
      </div>
    </>
  )
}

export default Search
